import React, { useEffect, useState } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { getVaptToolsStatusApi } from "../../../services/axios/AuthApis/VAPTApis";

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const STATUS_STYLES = {
  Ready: "text-green-700 bg-green-50",
  "Not Installed": "text-amber-700 bg-amber-50",
  "Not Configured": "text-gray-500 bg-gray-100",
};

// Display-only labels for the tool availability status returned by the API.
const STATUS_LABELS = {
  Ready: "Ready",
  "Not Installed": "Unavailable",
  "Not Configured": "Configuration required",
};

// Checkbox list of REAL tool_ids (vapt.models.ToolRegistry, live capability
// status via GET /api/vapt/tools/status/) scoped to a domain - never a
// hardcoded frontend tool list. selectedTools/onChange operate on real
// tool_id strings (e.g. "nuclei"), the same value the backend orchestration
// (vapt/tasks.py execute_tool_task, vapt/tool_executors.py) keys off of -
// sending display names here instead was the reason Start Scan never
// actually ran anything, real or demo.
const VaptToolSelector = ({ domain, selectedTools = [], onChange }) => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getVaptToolsStatusApi()
      .then((data) => {
        if (mounted) setTools(asArray(data));
      })
      .catch(() => {
        if (mounted) setTools([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const domainTools = tools.filter((t) => (t.domain || []).includes(domain));

  const toggle = (toolId) => {
    if (selectedTools.includes(toolId)) {
      onChange(selectedTools.filter((t) => t !== toolId));
    } else {
      onChange([...selectedTools, toolId]);
    }
  };

  if (!domain) {
    return <p className="text-sm text-gray-400">Select a domain first to see available tools.</p>;
  }
  if (loading) {
    return <p className="text-sm text-gray-400">Loading applicable tools…</p>;
  }
  if (domainTools.length === 0) {
    return <p className="text-sm text-gray-400">No tools are registered for the {domain} domain.</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {domainTools.map((t) => {
        const checked = selectedTools.includes(t.tool_id);
        return (
          <div
            key={t.tool_id}
            onClick={() => toggle(t.tool_id)}
            className={`flex items-center gap-3 px-4 py-2.5 border rounded-lg cursor-pointer transition-colors ${
              checked ? "border-primary-500 bg-primary-50" : "border-gray-200 hover:bg-gray-50"
            }`}
          >
            <div
              className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${
                checked ? "bg-primary-600 border-primary-600" : "border-gray-300"
              }`}
            >
              {checked && <CheckIcon className="w-3 h-3 text-white" />}
            </div>
            <span className="text-sm text-gray-800 flex-1">{t.name}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STATUS_STYLES[t.status] || "text-gray-500 bg-gray-100"}`}>
              {STATUS_LABELS[t.status] || t.status}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default VaptToolSelector;
